import { useState, useCallback } from 'react';
import { calendarAPI } from '../api/calendar';

/**
 * Hook personalizado para gestión del Calendario Laboral
 */
export const useCalendar = () => {
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ============================================================================
  // Configuración
  // ============================================================================

  /**
   * Cargar configuración de días laborables
   */
  const loadConfig = useCallback(async (departmentId = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.getConfig(departmentId);
      if (response.success) {
        setConfig(response.config);
        return { success: true, config: response.config };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar configuración';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Guardar configuración de días laborables
   */
  const saveConfig = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.saveConfig(data);
      if (response.success) {
        setConfig(response.config);
        console.log('success', response.message || 'Configuración guardada exitosamente');
        return { success: true, config: response.config };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al guardar configuración';
      setError(errorMsg);
      console.error('error', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // Eventos
  // ============================================================================

  /**
   * Cargar eventos con filtros
   */
  const loadEvents = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.getEvents(filters);
      if (response.success) {
        setEvents(response.events);
        return { success: true, events: response.events, total: response.total };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar eventos';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crear nuevo evento
   */
  const createEvent = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.createEvent(data);
      if (response.success) {
        setEvents(prev => [...prev, response.event].sort((a, b) =>
          new Date(a.fecha) - new Date(b.fecha)
        ));
        console.log('success', response.message || 'Evento creado exitosamente');
        return { success: true, event: response.event };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al crear evento';
      setError(errorMsg);
      console.error('error', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Actualizar evento existente
   */
  const updateEvent = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.updateEvent(id, data);
      if (response.success) {
        setEvents(prev => prev.map(evt =>
          evt.id === id ? response.event : evt
        ).sort((a, b) => new Date(a.fecha) - new Date(b.fecha)));
        console.log('success', response.message || 'Evento actualizado exitosamente');
        return { success: true, event: response.event };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al actualizar evento';
      setError(errorMsg);
      console.error('error', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Eliminar evento
   */
  const deleteEvent = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarAPI.deleteEvent(id);
      if (response.success) {
        setEvents(prev => prev.filter(evt => evt.id !== id));
        console.log('success', response.message || 'Evento eliminado exitosamente');
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al eliminar evento';
      setError(errorMsg);
      console.error('error', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // Utilidades
  // ============================================================================

  /**
   * Calcular días laborables entre fechas
   */
  const calcularDiasLaborables = useCallback(async (fechaInicio, fechaFin, departmentId = null) => {
    try {
      const response = await calendarAPI.calcularDiasLaborables(fechaInicio, fechaFin, departmentId);
      if (response.success) {
        return {
          success: true,
          dias: response.dias_laborables,
          fechaInicio: response.fecha_inicio,
          fechaFin: response.fecha_fin
        };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al calcular días laborables';
      return { success: false, error: errorMsg };
    }
  }, []);

  /**
   * Obtener próximo día laboral
   */
  const obtenerProximoDiaLaboral = useCallback(async (fecha, departmentId = null) => {
    try {
      const response = await calendarAPI.obtenerProximoDiaLaboral(fecha, departmentId);
      if (response.success) {
        return {
          success: true,
          fechaOriginal: response.fecha_original,
          proximoDia: response.proximo_dia_laboral
        };
      }
      return { success: false, error: response.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al obtener próximo día laboral';
      return { success: false, error: errorMsg };
    }
  }, []);

  /**
   * Limpiar estado
   */
  const clearState = useCallback(() => {
    setConfig(null);
    setEvents([]);
    setError(null);
  }, []);

  return {
    // Estado
    config,
    events,
    loading,
    error,

    // Métodos - Configuración
    loadConfig,
    saveConfig,

    // Métodos - Eventos
    loadEvents,
    createEvent,
    updateEvent,
    deleteEvent,

    // Métodos - Utilidades
    calcularDiasLaborables,
    obtenerProximoDiaLaboral,

    // Helpers
    clearState
  };
};

export default useCalendar;
